import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';

import NotFoundPage from '../components/404';
import PageData from '../helpers/pageData';
import Layout from '../components/Layouts/Layout';
import Body from '../components/ui/Body/Body';
import getPageContextFromData from '../lib/dto/getPageContextFromData';
import StructuredData from '../helpers/StructuredData';

const Event = ({
  data: { prismicEvent: { type, uid, data } },
}) => {
  if (!data) return <NotFoundPage />;

  const dataContext = getPageContextFromData(type, uid, data);

  return (
    <PageData.Provider value={dataContext}>
      <StructuredData
        type="Event"
        title={data.seo_title || data.title}
        description={data.seo_description || data.page_short_description}
        pathname={dataContext.url}
        image={data.landscape_image || data.thumbnail_image}
        startDate={data.start_date_time}
        endDate={data.end_date_time}
        location={data.address?.text}
      />
      <Layout>
        <Body />
      </Layout>
    </PageData.Provider>
  );
};

export const query = graphql`
query EventBySlug($uid: StringQueryOperatorInput = {}) {
  prismicEvent(uid: $uid) {
    _previewable
    uid
    type
    data {
      title
      introduction
      page_colour
      page_short_description
      prismic_page_name
      seo_description
      seo_title
      end_date_time
      start_date_time
      address {
        text
        richText
      }
      thumbnail_image {
        localFile {
          publicURL
          extension
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED,
              quality: 100,
              width: 640,
              breakpoints: [640, 960, 1400]
            )
          }
        }
      }
      landscape_image {
        localFile {
          publicURL
          extension
          childImageSharp {
            gatsbyImageData(
              layout: CONSTRAINED,
              quality: 100,
              width: 1400,
              breakpoints: [640, 960, 1400]
            )
          }
        }
      }
      body {
        ... on PrismicEventDataBodyContent {
          id
          slice_type
          primary {
            content_heading {
              html
              richText
              text
            }
            content_subtitle {
              richText
              html
              text
            }
            content_text {
              richText
              text
            }
            layout
          }
        }
        ... on PrismicEventDataBodyImage {
          id
          slice_type
          primary {
            alignment
            image {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
        }
        ... on PrismicEventDataBodyVideo {
          id
          slice_type
          primary {
            alignment
            video_embeded {
              embed_url
            }
          }
        }
        ... on PrismicEventDataBodyQuote {
          id
          slice_type
          primary {
            quote {
              richText
              text
            }
          }
        }
        ... on PrismicEventDataBodyBanner {
          id
          slice_type
          primary {
            background_colour
            text_colour
            big_text {
              richText
              text
            }
            text_link {
              richText
              text
            }
          }
        }
        ... on PrismicEventDataBodyList {
          id
          slice_type
          primary {
            list_heading {
              text
            }
          }
          items {
            list_items {
              document {
                ... on PrismicListItem {
                  id
                  data {
                    heading {
                      text
                    }
                    description {
                      text
                      richText
                    }
                    icon {
                      alt
                      url
                    }
                  }
                }
              }
            }
          }
        }
        ... on PrismicEventDataBodyTeamMembers {
          id
          slice_type
          primary {
            heading {
              text
            }
          }
          items {
            team_member {
              document {
                ... on PrismicTeamMember {
                  id
                  data {
                    name
                    title
                  }
                }
              }
            }
          }
        }
        ... on PrismicEventDataBodyRelatedContent {
          id
          slice_type
          primary {
            items_to_show
            related_content_title {
              text
            }
          }
        }
        ... on PrismicEventDataBodyDownloadFormHs {
          id
          slice_type
          primary {
            form_title
            hubspot_form_id
            form_image {
              localFile {
                publicURL
                extension
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED,
                    quality: 100,
                    width: 1400,
                    breakpoints: [640, 960, 1400]
                  )
                }
              }
            }
          }
        }
        ... on PrismicEventDataBodyEventbrite {
          id
          slice_type
          primary {
            eventbrite_event_url
            eventbrite_id
          }
        }
        ... on PrismicEventDataBodyHumanitix {
          id
          slice_type
          primary {
            humanitix_event_url
            humanitix_checkout_id
          }
        }
        ... on PrismicEventDataBodyActionButton {
          id
          slice_type
          primary {
            layout
            button_text {
              text
            }
            button_url {
              id
              link_type
              isBroken
              type
              uid
              url
              target
            }
          }
        }
      }
      process_tags {
        processes {
          slug
          type
        }
      }
      purpose_tags {
        purposes {
          type
          slug
        }
      }
      service_tags {
        services {
          type
          slug
        }
      }
    }
  }
}
`;

Event.propTypes = {
  data: PropTypes.shape({
    prismicEvent: PropTypes.object.isRequired,
  }).isRequired,
};

export default withPrismicPreview(Event);
